// @core/stores/userprofile/UserprofileBannerStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { userprofileApi } from '@core/api';
import type {
  ActivateBannerRequest,
  Banner,
  UpdateBannerRequest,
} from '@core/types/userprofile';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class UserprofileBannerStore {
  // List state
  items: Banner[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Banner | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Banner>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Banner | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Banner | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Set banner as active
   */
  async ActivateBanner(profile_id: string, banner_id: string, data: ActivateBannerRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.ActivateBanner(profile_id, banner_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new banner
   */
  async CreateBanner(file: File): Promise<Banner> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.CreateBanner(file);
      const item = response.banner;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete banner
   */
  async DeleteBanner(profile_id: string, banner_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await userprofileApi.DeleteBanner(profile_id, banner_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== banner_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === banner_id) {
          this.currentItem = null;
        }

        this.removeFromCache(banner_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get banner by ID
   */
  async GetBanner(profile_id: string, banner_id: string): Promise<Banner> {
    // Check cache first
    const cached = this.cache.get(banner_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetBanner(profile_id, banner_id);
      const item = response.banner;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List banners for profile
   */
  async ListBanners(profile_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListBanners(profile_id, params);

      runInAction(() => {
        this.items = response.banners?.banners ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update banner
   */
  async UpdateBanner(profile_id: string, banner_id: string, data: UpdateBannerRequest): Promise<Banner> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.UpdateBanner(profile_id, banner_id, data);
      const item = response.banner;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const userprofileBannerStore = new UserprofileBannerStore();
